import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { isEqualWith, isNumber } from "lodash";

import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import FormLayout from "components/form/FormLayout";
import Form from "components/form/Form";
import PresetAndCustomColorPicker from "components/colorpicker/PresetAndCustomColorPicker";
import { GetBookingCategory } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { DeleteCustomerType, GetCustomerType, PutCustomerType } from "api/rpc/2024-04/facilityAdmin/customer/type";
import Toggle from "components/form/toggle/Toggle";

import { IDiscount } from "api/rpc/2024-04/facilityAdmin/product/discount";
import AutomaticDiscountSelect from "./components/AutomaticDiscountSelect";
import { TCustomerType } from "redux/reducers/models/facility";
import { IMultipleActionDropdownPageAction } from "components/page/PageActions";

interface ICustomerTypeState {
  id: number;
  title: string;
  subtitle: string;
  application: string;
  color: string;
  default_booking_category_id: number;
  migrate_customer_type_id: number;
  discounts: IDiscount[];
  default: boolean;
  guest_customer_type_id: number;
}

export default function CustomerTypesEdit() {
  const history = useHistory();
  const { t } = useTranslation();
  const { Option } = Select;
  const { id } = useParams<{ id: string }>();
  const [customerTypeState, setCustomerTypeState] = useState<ICustomerTypeState>({
    id: undefined,
    title: "",
    subtitle: "",
    application: "",
    color: "",
    migrate_customer_type_id: undefined,
    default_booking_category_id: undefined,
    discounts: [],
    default: false,
    guest_customer_type_id: undefined,
  });

  const selectDiscountCallback = (data: IDiscount[]) => {
    setCustomerTypeState(prev => ({ ...prev, discounts: data }));
  };

  const [prevCustomerTypeState, setPrevCustomerTypeState] = useState<ICustomerTypeState>(customerTypeState);
  const [dropDownState, setDropDownState] = useState({ customerType: [] });

  const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);
  const [discountModalVisibility, setDiscountModalVisibility] = useState(false);

  const [customerTypeStateBeforeChanges, setCustomerTypeStateBeforeChanges] = useState<ICustomerTypeState>(undefined);
  const [customerTypeStateLoaded, setCustomerTypeStateLoaded] = useState<boolean>(false);

  const [allCustomerTypes, setAllCustomerTypes] = useState<TCustomerType[]>([]);

  const [bookingCategoryState, setBookingCategoryState] = useState({
    bookingCategories: [],
  });

  useEffect(() => {
    void loadCustomerTypes();
    void loadBookingCategories();

    return () => {
      setCustomerTypeState({
        id: undefined,
        title: "",
        subtitle: "",
        application: "",
        color: "",
        migrate_customer_type_id: undefined,
        default_booking_category_id: undefined,
        discounts: [],
        default: false,
        guest_customer_type_id: undefined,
      });
      setPrevCustomerTypeState({
        id: undefined,
        title: "",
        subtitle: "",
        application: "",
        color: "",
        migrate_customer_type_id: undefined,
        default_booking_category_id: undefined,
        discounts: [],
        default: false,
        guest_customer_type_id: undefined,
      });
      setDropDownState({ customerType: [] });
    };
  }, []);

  async function loadCustomerTypes() {
    const res = await GetCustomerType({ discounts: true }, true);

    if (res.status !== StatusCode.OK) {
      return;
    }

    setDropDownState(prev => ({
      ...prev,
      customerType: res.data,
    }));

    const currentType = res.data.find(type => type.id === Number(id));
    setCustomerTypeState(prev => ({
      ...prev,
      id: currentType.id,
      title: currentType.title,
      subtitle: currentType.subtitle,
      application: currentType.application,
      color: currentType.color,
      default_booking_category_id: currentType.default_booking_category_id,
      discounts: currentType.discounts,
      default: currentType.default,
      guest_customer_type_id: currentType.guest_customer_type_id,
    }));

    setPrevCustomerTypeState(prev => ({
      ...prev,
      id: currentType.id,
      title: currentType.title,
      subtitle: currentType.subtitle,
      application: currentType.application,
      color: currentType.color,
      default_booking_category_id: currentType.default_booking_category_id,
      discounts: currentType.discounts,
      default: currentType.default,
      guest_customer_type_id: currentType.guest_customer_type_id,
    }));

    setAllCustomerTypes(res.data.filter(type => type.id !== Number(id)).filter(type => type.application === currentType.application));

    setCustomerTypeStateLoaded(true);
  }

  async function loadBookingCategories() {
    const res = await GetBookingCategory(null, false);
    if (res.status !== StatusCode.OK) {
      return;
    }

    setBookingCategoryState(prev => ({
      ...prev,
      bookingCategories: res.data,
    }));
  }

  async function saveCustomerType() {
    const params = {
      id: customerTypeState?.id,
      title: customerTypeState?.title,
      subtitle: customerTypeState.subtitle,
      color: customerTypeState.color,
      discount_ids: customerTypeState?.discounts.map(d => d.id),
      default_booking_category_id: customerTypeState.default_booking_category_id,
      default: customerTypeState.default,
      guest_customer_type_id: customerTypeState.guest_customer_type_id,
    };

    const res = await PutCustomerType(params, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/customer_types");
  }

  async function removeCustomerType() {
    if (prevCustomerTypeState.migrate_customer_type_id === undefined) {
      return;
    }

    const res = await DeleteCustomerType(prevCustomerTypeState, true);
    if (res.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/customer_types");
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setCustomerTypeState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleChange(event: any) {
    const { id, checked } = event.target;
    setCustomerTypeState(prevState => ({ ...prevState, [id]: checked }));
  }

  function handleDeleteModalVisibility() {
    setDeleteModalVisibility(!deleteModalVisibility);
  }

  function handleBookingCategoryChange(value: any) {
    setCustomerTypeState(prevState => ({
      ...prevState,
      default_booking_category_id: value,
    }));
  }

  function handleGuestCustomerTypeChange(value: any) {
    if (value === 0) {
      value = null;
    }
    setCustomerTypeState(prevState => ({
      ...prevState,
      guest_customer_type_id: value,
    }));
  }

  function handleMigratedCustomerIdChange(id: any) {
    setPrevCustomerTypeState(prev => ({
      ...prev,
      migrate_customer_type_id: id,
    }));
  }

  function handleColorPick(value: string) {
    setCustomerTypeState(prevState => ({
      ...prevState,
      color: value,
    }));
  }

  const secondaryActions: IMultipleActionDropdownPageAction = {
    label: t("Options"),
    dropdownProps: {
      alignment: "left",
      options: [
        {
          type: "handler",
          label: t("Delete"),
          handler: () => handleDeleteModalVisibility(),
          icon: "trash",
        },
        {
          type: "handler",
          label: t("Discounts"),
          handler: () => setDiscountModalVisibility(true),
          icon: "percentage",
        },
      ],
    },
  };

  function unsavedChangesExist() {
    if (customerTypeStateBeforeChanges === undefined) {
      if (customerTypeStateLoaded) {
        setCustomerTypeStateBeforeChanges(customerTypeState);
      }
      return false;
    }

    return !isEqualWith(customerTypeStateBeforeChanges, customerTypeState);
  }

  function cancelUnsavedChanges() {
    setCustomerTypeState(customerTypeStateBeforeChanges);
  }

  return (
    <div>
      <Page
        title={t("Edit Customer Type: ") + prevCustomerTypeState.title}
        narrow
        multipleActionDropdownAction={secondaryActions}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Customer Types"),
            url: "/admin/settings/customer_types",
          },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveCustomerType,
          onCancel: cancelUnsavedChanges,
        }}
      >
        <Card>
          <Card.Section>
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={customerTypeState.title ?? ""}
                    label={t("Title")}
                    id="title"
                    onChange={handleInputChange}
                    placeholder={t("Title")}
                  />
                  <Input
                    value={customerTypeState.subtitle ?? ""}
                    label={t("Subtitle")}
                    id="subtitle"
                    onChange={handleInputChange}
                    placeholder={t("Subtitle")}
                  />
                </FormLayout.Group>
                <FormLayout.Group alignStart>
                  {customerTypeState.application === "green_fee" && (
                    <Select
                      label={t("Default Booking Category")}
                      onChange={handleBookingCategoryChange}
                      defaultValue={customerTypeState.default_booking_category_id}
                    >
                      {bookingCategoryState.bookingCategories.map((category: any, index: number) => {
                        return (
                          <Option key={index} value={category.id} name={category.title}>
                            <span>{category.title}</span>
                          </Option>
                        );
                      })}
                    </Select>
                  )}
                  <Select
                    label={t("Guest Customer Type")}
                    onChange={handleGuestCustomerTypeChange}
                    defaultValue={customerTypeState.guest_customer_type_id}
                    helpText={t("Automatically assigned to guests on this customer type's bookings.")}
                  >
                    <Option value={0} name={t("None")}>
                      <span>{t("None")}</span>
                    </Option>
                    {allCustomerTypes.map((customerType: any, index: number) => {
                      return (
                        <Option key={index} value={customerType.id} name={customerType.title}>
                          <span>{customerType.title}</span>
                        </Option>
                      );
                    })}
                  </Select>
                </FormLayout.Group>
                <FormLayout.Group>
                  <Toggle
                    labelRight={t("Default")}
                    id="default"
                    checked={customerTypeState.default}
                    onChange={handleToggleChange}
                    labelSupport={t("This customer type will be selected by default for all new customers.")}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Form>
          </Card.Section>
        </Card>

        <Card
          title={t("Tee Sheet Color")}
          subtitle={t("Select a color for this customer type to use on the tee sheet")}
          titleActions={[
            {
              content: t("Remove Color"),
              action: () => handleColorPick(""),
              buttonType: "secondary",
              disabled: !customerTypeState.color,
            },
          ]}
        >
          <Card.Section>
            <PresetAndCustomColorPicker
              colorValue={customerTypeState.color}
              onChange={updatedHexColor => handleColorPick(updatedHexColor ?? "")}
            />
          </Card.Section>
        </Card>
      </Page>

      <Sheet
        closable
        onCancel={handleDeleteModalVisibility}
        size="small"
        open={deleteModalVisibility}
        cancelText={t("Close")}
        okText={t("Delete")}
        title={t("Deletion Confirmation")}
        onOk={isNumber(prevCustomerTypeState.migrate_customer_type_id) ? removeCustomerType : undefined}
        overflow
      >
        <div className="text-left p-3">
          <Select
            label={t("Choose customer type to migrate to:")}
            onChange={handleMigratedCustomerIdChange}
            placeholder={t("Select customer type")}
          >
            {dropDownState.customerType
              ?.filter((type: any) => type.id !== prevCustomerTypeState.id)
              .map((type: any, index: number) => {
                return (
                  <Option key={index} value={type.id} name={type.title}>
                    <span>{type.title}</span>
                  </Option>
                );
              })}
          </Select>
        </div>
      </Sheet>

      <Sheet
        closable
        onCancel={() => setDiscountModalVisibility(false)}
        size="medium"
        open={discountModalVisibility}
        cancelText={"Close"}
        okText={"Confirm"}
        title={t("Automatic Discounts")}
        onOk={() => setDiscountModalVisibility(false)}
        overflow
      >
        <AutomaticDiscountSelect
          onDiscountSelect={selectDiscountCallback}
          selectedDiscounts={customerTypeState.discounts}
        />
      </Sheet>
    </div>
  );
}
